/**
 * AutoEngineSummary — the running option AUTO-TRADE engines (DeltaMiner / ThetaMiner /
 * Rudra / …) shown inside the unified Options-hub Monitor, so "your trades" and "the
 * automated strategies" live in one place with ONE visual language.
 *
 * Role split (kept crisp so there's no "where do I land?" confusion):
 *   • Here (Options hub) = SEE & ACT FAST — a glance at each engine's state + P&L, with a
 *     one-click jump to its full monitor.
 *   • Options-AT page    = CONFIGURE & DEEP-MANAGE — create, settings, rules, backtest, the
 *     rich trade grid, decision log, LIVE toggle and square-off.
 *
 * P&L here reuses the data already returned by the list call (cycle economics) — no extra
 * per-instance fetch.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { optionStrategyService } from '../../services/optionStrategy.service';
import { PlainStatus, useOptionColors } from './shared/OptionsUi';
import { Cycle, cycleStats } from './cycleFormat';

interface Instance {
  id: number; name: string; engine: string; multiLeg?: boolean;
  config?: { tradingMode?: 'PAPER' | 'LIVE' };
  schedules?: Array<{ enabled?: boolean }>;
  positions?: Array<{ status?: string; realizedPnl?: number }>;
  cycles?: Cycle[];
}

const statOf = (inst: Instance) => {
  if (inst.multiLeg) return cycleStats(inst.cycles || []);
  const positions = inst.positions || [];
  const realised = positions.reduce((s, p) => s + (Number(p.realizedPnl) || 0), 0);
  return { trades: 0, openCount: positions.filter(p => p.status && p.status !== 'FLAT').length, realised, open: 0, total: Math.round(realised), lastTradePnl: null as number | null };
};
const inrShort = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

const AutoEngineSummary: React.FC = () => {
  const navigate = useNavigate();
  const c = useOptionColors();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await optionStrategyService.list(); setInstances(r.data?.data || []); }
    catch { /* keep last */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30_000); return () => clearInterval(id); }, [load]);

  const totals = useMemo(() => {
    const running = instances.filter(i => i.schedules?.[0]?.enabled);
    let open = 0, total = 0;
    for (const i of instances) { const s = statOf(i); open += s.openCount; total += s.total; }
    return { running: running.length, count: instances.length, open, total };
  }, [instances]);

  if (!loading && instances.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800 }}>Auto-trade engines</Typography>
        <Chip size="small" label={`${totals.running}/${totals.count} running`} color={totals.running ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700 }} />
        {totals.open > 0 && <Chip size="small" label={`${totals.open} open`} color="info" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700 }} />}
        <Chip size="small" label={`P&L ${inrShort(totals.total)}`} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800, bgcolor: totals.total >= 0 ? c.profitBg : c.lossBg, color: totals.total >= 0 ? c.profit : c.loss }} />
        {loading && <CircularProgress size={13} />}
        <Button size="small" variant="text" endIcon={<OpenInNewIcon fontSize="small" />} sx={{ ml: 'auto' }} onClick={() => navigate('/option-strategies')}>Configure &amp; manage</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Fully-automated option strategies that enter and exit on their own. This is a quick glance — open the full monitor for the trade-by-trade grid, decision log, settings, rules, backtest and square-off.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        {instances.map(inst => {
          const running = !!inst.schedules?.[0]?.enabled;
          const live = inst.config?.tradingMode === 'LIVE';
          const s = statOf(inst);
          const status: { tone: 'good' | 'bad' | 'warn' | 'neutral'; emoji: string; text: string } = !running
            ? { tone: 'neutral', emoji: '⏸️', text: 'Paused — not scanning. Open positions (if any) are still managed.' }
            : s.openCount > 0
              ? { tone: 'good', emoji: '✅', text: `Running — ${s.openCount} position${s.openCount === 1 ? '' : 's'} open and being managed.` }
              : { tone: 'neutral', emoji: '🟢', text: 'Running — waiting for its next entry signal.' };
          return (
            <Card key={inst.id} variant="outlined" sx={{ borderLeft: `4px solid ${running ? c.profit : c.neutral}`, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
              onClick={() => navigate('/option-strategies')}>
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.6, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{inst.name}</Typography>
                  <Chip size="small" label={inst.engine} variant="outlined" sx={{ height: 16, fontSize: '0.54rem' }} />
                  <Chip size="small" label={live ? 'LIVE' : 'PAPER'} color={live ? 'error' : 'warning'} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800 }} />
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    {s.trades > 0 && <Typography sx={{ fontSize: '0.56rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.3 }}>{s.trades} trade{s.trades === 1 ? '' : 's'}</Typography>}
                    <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: s.total >= 0 ? c.profit : c.loss }}>{inrShort(s.total)}</Typography>
                  </Box>
                </Box>
                <PlainStatus tone={status.tone} emoji={status.emoji}>{status.text}</PlainStatus>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default AutoEngineSummary;
